/**
 * **Die gesetzlichen Feiertage in Nordrhein-Westfalen**
 * ([Z-19](../../../docs/phasen/zusatz-19-tagesplan.md) AP 19.7).
 *
 * `zeit.ts` trug bis heute diese Zusage:
 *
 * > *„Feiertage kennt Work nicht … Ein fest verdrahteter Feiertagskalender wäre geraten, und für
 * > eine Frist, die ohnehin grob ist, ist ein Tag Ungenauigkeit billiger als ein Kalender, den
 * > niemand pflegt."*
 *
 * **Das stimmte, solange Work nur Fristen rechnete.** Eine Frist ist eine Aussage über einen
 * Zeitraum, und ein Tag Ungenauigkeit darin merkt niemand. Seit Z-19 zeichnet Work aber einen
 * **Tagesplan** – und ein Block „IOR 2.0, 9:00–13:00" am 3. Oktober ist kein Tag Ungenauigkeit,
 * sondern sichtbar falsch. Wer so einen Plan einmal sieht, glaubt dem nächsten auch nicht mehr.
 *
 * ## Warum gerechnet und nicht gepflegt
 *
 * Der zweite Teil der alten Begründung – *„ein Kalender, den niemand pflegt"* – bleibt richtig.
 * Deshalb steht hier **keine Liste von Daten**, die jemand jedes Jahr nachtragen müsste, sondern
 * die Rechnung: sechs feste Tage im Jahr und fünf, die am Osterdatum hängen. Das gilt für 2026
 * genauso wie für 2031, ohne dass jemand etwas tut.
 *
 * ## Warum NRW und nicht „Deutschland"
 *
 * Es gibt keine deutschen Feiertage, nur Landesrecht plus einen einzigen bundesweiten (den
 * 3. Oktober). **Fronleichnam** und **Allerheiligen** sind in NRW frei, in Niedersachsen nicht;
 * **Reformationstag** und **Buß- und Bettag** sind es umgekehrt. NetCon sitzt in NRW, also gilt
 * NRW. Sollte je ein Standort in einem anderen Land dazukommen, ist die Erweiterung ein
 * Parameter an {@link feiertageDesJahres} – nicht ein Umbau.
 */

/**
 * **Ostersonntag** nach dem Gaußschen Algorithmus in der Fassung von Meeus/Jones/Butcher.
 *
 * Fünf der elf Feiertage hängen daran, und Ostern ist der einzige Teil dieses Moduls, den man
 * nicht nachschlagen kann: Es ist der erste Sonntag nach dem ersten Frühlingsvollmond, und
 * dieser Vollmond ist ein kirchlich definierter, kein astronomischer.
 *
 * Die Zwischenwerte tragen bewusst ihre Buchstaben aus der Literatur statt sprechender Namen –
 * wer die Rechnung prüfen will, vergleicht sie mit der Quelle, und dabei hilft `h` mehr als
 * `mondkorrektur`. Geprüft ist sie an vier bekannten Jahren in `feiertage.test.ts`.
 */
export function ostersonntag(jahr: number): Date {
  const a = jahr % 19
  const b = Math.floor(jahr / 100)
  const c = jahr % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const zaehler = h + l - 7 * m + 114
  return new Date(jahr, Math.floor(zaehler / 31) - 1, (zaehler % 31) + 1)
}

/** Ein Feiertag mit seinem Namen – der Name ist die halbe Auskunft („heute ist Fronleichnam"). */
export interface Feiertag {
  /** Tagesschlüssel `2026-10-03`. */
  tag: string
  name: string
}

/** `2026-10-03` aus einem `Date` – als Kalendertag in Ortszeit, nicht über UTC. */
const schluessel = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

/** `anzahl` Tage nach `d`, als neuer Kalendertag. */
const plus = (d: Date, anzahl: number): Date =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate() + anzahl)

/**
 * **Alle gesetzlichen Feiertage eines Jahres in NRW** – sechs feste, fünf bewegliche.
 *
 * Die beweglichen in Tagen nach Ostersonntag: Karfreitag −2, Ostermontag +1, Christi
 * Himmelfahrt +39, Pfingstmontag +50, Fronleichnam +60.
 *
 * **Ostersonntag und Pfingstsonntag fehlen mit Absicht:** Sie sind Sonntage und damit ohnehin
 * keine Arbeitstage. Sie hier aufzuführen würde die Liste auf dreizehn verlängern, ohne dass
 * sich irgendetwas daran änderte, was Work rechnet – und wer sie sucht, findet in genau dieser
 * Zeile die Erklärung.
 */
export function feiertageDesJahres(jahr: number): Feiertag[] {
  const o = ostersonntag(jahr)
  const fest: [number, number, string][] = [
    [1, 1, 'Neujahr'],
    [5, 1, 'Tag der Arbeit'],
    [10, 3, 'Tag der Deutschen Einheit'],
    [11, 1, 'Allerheiligen'],
    [12, 25, '1. Weihnachtstag'],
    [12, 26, '2. Weihnachtstag'],
  ]
  const beweglich: [number, string][] = [
    [-2, 'Karfreitag'],
    [1, 'Ostermontag'],
    [39, 'Christi Himmelfahrt'],
    [50, 'Pfingstmontag'],
    [60, 'Fronleichnam'],
  ]
  return [
    ...fest.map(([monat, tag, name]) => ({
      tag: schluessel(new Date(jahr, monat - 1, tag)),
      name,
    })),
    ...beweglich.map(([abstand, name]) => ({ tag: schluessel(plus(o, abstand)), name })),
  ].sort((x, y) => x.tag.localeCompare(y.tag))
}

/**
 * **Zwischenspeicher je Jahr.**
 *
 * `istFeiertag` wird beim Planen eines Monats mehrere hundert Mal gerufen; das Osterdatum
 * jedes Mal neu auszurechnen wäre Verschwendung. Ein `Map` je Jahr genügt – die Anwendung lebt
 * im Browser eine Sitzung lang und fragt dabei nach zwei, höchstens drei Jahren.
 */
const jahresspeicher = new Map<number, Map<string, string>>()

function jahr(jahreszahl: number): Map<string, string> {
  let vorhanden = jahresspeicher.get(jahreszahl)
  if (!vorhanden) {
    vorhanden = new Map(feiertageDesJahres(jahreszahl).map((f) => [f.tag, f.name]))
    jahresspeicher.set(jahreszahl, vorhanden)
  }
  return vorhanden
}

/** Ist dieser Kalendertag in NRW ein gesetzlicher Feiertag? */
export function istFeiertag(d: Date): boolean {
  return jahr(d.getFullYear()).has(schluessel(d))
}

/** Der Name des Feiertags, oder `null` – für die Anzeige: „Heute ist Fronleichnam". */
export function feiertagName(d: Date): string | null {
  return jahr(d.getFullYear()).get(schluessel(d)) ?? null
}

/** Dieselbe Frage für einen Tagesschlüssel `2026-10-03` – die Auswertung rechnet mit Schlüsseln. */
export function istFeiertagsschluessel(tag: string): boolean {
  const jahreszahl = Number(tag.slice(0, 4))
  return Number.isFinite(jahreszahl) && jahr(jahreszahl).has(tag)
}
